/* eslint-disable react/no-did-mount-set-state */
import React, { Component } from 'react';
import classnames from 'classnames';
import { isNumber } from 'lodash';
import isImage from 'is-image';
import LazyLoad from 'react-lazyload';

import * as mediaServer from 'Components/utils/media-server';
import { getDOMAspect, getGridCellSize } from 'Components/utils/responsive/Responsive.helper';
import ratioUtils from 'Components/utils/ratios/ratios.utils';

import { ImageInvalid } from 'Components/frameworks/image-invalid/ImageInvalid.jsx';
import { ImagePlaceholder } from 'Components/frameworks/image-placeholder/ImagePlaceholder.jsx';

const WEBP_TYPE = 'image/webp';
const RETINA_FACTOR = 1.5;
const IMAGE_QUALITY = '60';

const lazyloadProps = { height: 'auto', offset: 200, once: true };

const sizeCache = {};

function getRetinaHeight(height) {
  return isNumber(height) ? RETINA_FACTOR * height : height;
}

class Image extends Component<Props> {
  static defaultProps = {
    altMsg: '',
    forceImage: false,
    groupKey: null,
    hasLazyLoading: true,
    transformation: 'crop',
  };

  constructor(props) {
    super(props);

    this.targetImageUrls = {};
    this.imageRatio = 0;
    this.sizeCacheKey = this.getSizeCacheKey(props);
    this.state = {
      mounted: false,
    };
  }

  componentDidMount() {
    this.targetImageUrls = this.getTargetImageUrls(this.props, true);
    this.setState({ mounted: true });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.imageUrl !== this.props.imageUrl ||
      nextProps.ratioKey !== this.props.ratioKey ||
      nextProps.transformation !== this.props.transformation
    ) {
      this.sizeCacheKey = this.getSizeCacheKey(nextProps);
      this.targetImageUrls = this.getTargetImageUrls(nextProps, true);
    }
  }

  getSizeCacheKey(props) {
    const { groupKey, ratioKey, transformation } = props;

    if (!groupKey) {
      return null;
    }

    return `${groupKey}-${ratioKey}-${transformation}`;
  }

  getSizeParams(props, isMounted) {
    if ((this.sizeCacheKey && !sizeCache[this.sizeCacheKey]) || !this.sizeCacheKey) {
      const desiredWidth = this.getDesiredWidth(props, isMounted);
      const desiredTransform = this.getDesiredTransformation(props);
      const desiredHeight = this.getDesiredHeight(props, desiredWidth, this.getDesiredRatio(props));

      if (!this.sizeCacheKey) {
        return {
          desiredWidth,
          desiredTransform,
          desiredHeight,
        };
      }

      sizeCache[this.sizeCacheKey] = {
        desiredWidth,
        desiredTransform,
        desiredHeight,
      };
    }

    return sizeCache[this.sizeCacheKey];
  }

  getTargetImageUrls(props, isMounted) {
    if (!props.imageUrl) return null;
    if (props.dontChangeAnything) {
      return {
        jpg1x: props.imageUrl,
        jpg2x: props.imageUrl,
        webp1x: props.imageUrl,
        webp2x: props.imageUrl,
      };
    }

    const { desiredWidth, desiredTransform, desiredHeight } = this.getSizeParams(props, isMounted);

    const jpg1x = mediaServer.getUrl({
      url: props.imageUrl,
      width: desiredWidth,
      height: desiredHeight,
      transform: desiredTransform,
      format: 'jpeg',
      quality: IMAGE_QUALITY,
      gravity: 'center',
    });
    const jpg2x = mediaServer.getUrl({
      url: props.imageUrl,
      width: RETINA_FACTOR * desiredWidth,
      height: getRetinaHeight(desiredHeight),
      transform: desiredTransform,
      format: 'jpeg',
      quality: IMAGE_QUALITY,
      gravity: 'center',
    });
    const webp1x = mediaServer.getUrl({
      url: props.imageUrl,
      width: desiredWidth,
      height: desiredHeight,
      transform: desiredTransform,
      format: 'webp',
      quality: IMAGE_QUALITY,
      gravity: 'center',
    });
    const webp2x = mediaServer.getUrl({
      url: props.imageUrl,
      width: RETINA_FACTOR * desiredWidth,
      height: getRetinaHeight(desiredHeight),
      transform: desiredTransform,
      format: 'webp',
      quality: IMAGE_QUALITY,
      gravity: 'center',
    });

    return {
      jpg1x,
      jpg2x,
      webp1x,
      webp2x,
    };
  }

  getDesiredWidth(props, isMounted) {
    if (props.mandatoryWidthAndHeight) {
      return props.mandatoryWidthAndHeight.w;
    }
    const gridCellSize = getGridCellSize(this);
    return !!gridCellSize && gridCellSize.width
      ? gridCellSize.width
      : getDOMAspect(isMounted).viewportWidthPX;
  }

  getDesiredHeight(props, desiredWidth, desiredRatio) {
    if (props.mandatoryWidthAndHeight && props.mandatoryWidthAndHeight.h) {
      return props.mandatoryWidthAndHeight.h;
    }
    if (props.changeWidthButHeightIsAuto) {
      return 'auto';
    }

    return Math.floor(desiredWidth * desiredRatio);
  }

  getDesiredRatio(props) {
    if (props.mandatoryWidthAndHeight && isNumber(props.mandatoryWidthAndHeight.h)) {
      return props.mandatoryWidthAndHeight.h / props.mandatoryWidthAndHeight.w;
    }

    if (props.ratioKey) {
      return ratioUtils.values[props.ratioKey];
    }
  }

  getDesiredTransformation(props) {
    if (props.changeWidthButHeightIsAuto) {
      return 'crop';
    }

    return props.transformation;
  }

  withLazyLoad(node) {
    return this.props.hasLazyLoading ? <LazyLoad {...lazyloadProps}>{node}</LazyLoad> : node;
  }

  renderSimpleIMG(className) {
    return this.withLazyLoad(
      <img
        data-testid="img"
        alt={this.props.altMsg}
        className={classnames(className, 'max-w-full')}
        src={this.targetImageUrls.jpg1x}
        style={this.props.style}
      />,
    );
  }

  renderSrcSetIMG() {
    return this.withLazyLoad(
      <picture data-testid="img-srcset">
        <source
          type={WEBP_TYPE}
          srcSet={`${this.targetImageUrls.webp1x} 1x, ${this.targetImageUrls.webp2x} 2x`}
        />
        <img
          alt={this.props.altMsg}
          className={classnames('max-w-full', {
            'block h-full w-full': this.imageRatio,
          })}
          src={this.targetImageUrls.jpg1x}
          srcSet={`${this.targetImageUrls.jpg2x} 2x`}
        />
      </picture>,
    );
  }

  shouldRenderImage() {
    return this.state.mounted && (this.props.forceImage || isImage(this.props.imageUrl));
  }

  render() {
    if (!this.props.imageUrl) return null;
    this.imageRatio = this.getDesiredRatio(this.props);

    const aspectRatio = 100 * this.imageRatio;

    if (this.shouldRenderImage()) {
      if (this.imageRatio) {
        return (
          <ImagePlaceholder className={this.props.className} aspectRatio={aspectRatio}>
            {this.props.dontChangeAnything ? this.renderSimpleIMG() : this.renderSrcSetIMG()}
          </ImagePlaceholder>
        );
      }
      return this.renderSimpleIMG(this.props.className);
    }

    return <ImageInvalid aspectRatio={aspectRatio} className={this.props.className} />;
  }
}

export { Image as default, sizeCache };
